import { Controller } from "@hotwired/stimulus";
import { Modal } from "bootstrap";

// Drives the single per-index "Archive media" modal. The list rows carry the
// triggers (data-action="media-archive-modal#open / #restore"); the open modal
// lazy-loads its body by pointing the inner turbo-frame's src at the per-media
// usages URL (Turbo Drive is off app-wide, so the frame is driven from JS — the
// same trick media_hub_controller uses). Archive/restore are tiny fetches; on
// success we reload the list frame via a window event and show a snackbar. No
// markup is built in JS: the body is server-rendered into the frame and the
// snackbars are cloned from <template>s.
const RELOAD_EVENT = "media:list-changed";

export default class extends Controller {
  static targets = ["modal", "title", "error", "successToast", "errorToast"];

  // Open the modal for a media: set the title, point the body frame at the
  // usages URL, and show it. The body (incl. the Archive form button) renders
  // server-side into the frame.
  open(event) {
    if (event) event.preventDefault();
    const { url, title } = event.params;

    if (this.hasTitleTarget && title) this.titleTarget.textContent = title;
    this.loadFrame(url);

    this.modal().show();
  }

  // Archive submit, bound on the body's Archive button (rendered into the frame).
  // PATCHes the archive URL; on success closes the modal, reloads the list, and
  // shows a snackbar. On failure keeps the modal open and reveals the error line.
  async submit(event) {
    if (event) event.preventDefault();
    const url = event.params.url;
    this.hideError();

    try {
      const response = await this.patch(url);
      if (!response.ok) return this.showError();

      this.modal().hide();
      this.notifyListChanged();
      this.showSnackbar("success", "Media archived");
    } catch {
      this.showError();
    }
  }

  // Restore is a list-row action with no modal — restoring is always safe.
  async restore(event) {
    if (event) event.preventDefault();
    const url = event.params.url;

    try {
      const response = await this.patch(url);
      if (!response.ok) return this.showSnackbar("error", "Couldn't restore this media");

      this.notifyListChanged();
      this.showSnackbar("success", "Media restored");
    } catch {
      this.showSnackbar("error", "Couldn't restore this media");
    }
  }

  loadFrame(url) {
    const frame = document.getElementById("archive_media_modal_frame");
    if (frame) frame.src = url;
  }

  patch(url) {
    return fetch(url, {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": this.csrfToken(),
        Accept: "application/json"
      }
    });
  }

  notifyListChanged() {
    window.dispatchEvent(new CustomEvent(RELOAD_EVENT));
  }

  modal() {
    return Modal.getOrCreateInstance(this.modalTarget);
  }

  showError() {
    if (this.hasErrorTarget) this.errorTarget.hidden = false;
  }

  hideError() {
    if (this.hasErrorTarget) this.errorTarget.hidden = true;
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content;
  }

  // Clone the matching <template> snackbar into the global flash container and
  // set its title — never construct the markup in JS.
  showSnackbar(type, title) {
    const template = type === "error"
      ? (this.hasErrorToastTarget ? this.errorToastTarget : null)
      : (this.hasSuccessToastTarget ? this.successToastTarget : null);
    if (!template) return;

    const node = template.content.firstElementChild.cloneNode(true);
    const titleEl = node.querySelector(".snackbar-title span");
    if (titleEl) titleEl.textContent = title;

    this.flashContainer().appendChild(node);
  }

  flashContainer() {
    let container = document.querySelector(".app-flash-alert-container");
    if (!container) {
      container = document.createElement("div");
      container.className = "app-flash-alert-container";
      document.body.appendChild(container);
    }
    return container;
  }
}
